import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import LanguageSwitcher from '../../../components/LanguageSwitcher';
import { useTranslation } from 'react-i18next';

export default function Navbar() {
  const { t } = useTranslation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileMenuOpen]);

  const navLinks = [
    { name: t('nav.home'), path: '/', ariaLabel: 'Accueil - Transport privé Genève Lyon' },
    { name: t('nav.services'), path: '/services', ariaLabel: 'Services VTC premium stations ski' },
    { name: t('nav.pricing'), path: '/tarifs', ariaLabel: 'Tarifs transfert aéroport' },
    { name: t('nav.stations'), path: '/stations', ariaLabel: 'Stations de ski desservies' },
    { name: 'Galerie', path: '/galerie', ariaLabel: 'Galerie photos véhicules et services' },
    { name: t('nav.contact'), path: '/contact', ariaLabel: 'Contact et réservation' }
  ];

  return (
    <>
      <motion.nav
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.5, ease: [0.43, 0.13, 0.23, 0.96] }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled
            ? 'bg-white shadow-lg py-3 sm:py-4'
            : 'bg-transparent py-4 sm:py-6'
        }`}
        role="navigation"
        aria-label="Navigation principale"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <a 
              href="/" 
              className="flex items-center gap-2 sm:gap-3 group"
              aria-label="Voyage Elite - Retour à l'accueil"
            >
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-voyage-gold to-amber-600 rounded-full flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                <i className="ri-car-line text-white text-xl sm:text-2xl" aria-hidden="true"></i>
              </div>
              <div>
                <div className={`font-playfair font-bold text-lg sm:text-xl transition-colors ${
                  isScrolled ? 'text-night-blue' : 'text-white'
                }`}>
                  Voyage Elite
                </div>
                <div className={`font-inter text-xs transition-colors ${
                  isScrolled ? 'text-slate-600' : 'text-white/90'
                }`}>
                  Premium Service
                </div>
              </div>
            </a>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-1" role="menubar">
              {navLinks.map((link, index) => (
                <motion.a
                  key={link.name}
                  href={link.path}
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05, duration: 0.4 }}
                  className={`px-3 xl:px-4 py-2.5 rounded-lg font-inter text-sm font-medium transition-all hover:scale-105 whitespace-nowrap relative group ${
                    isScrolled
                      ? 'text-night-blue hover:text-voyage-gold'
                      : 'text-white hover:text-voyage-gold'
                  }`}
                  role="menuitem"
                  aria-label={link.ariaLabel}
                >
                  {link.name}
                  <span className="absolute bottom-1 left-1/2 -translate-x-1/2 w-0 h-0.5 bg-voyage-gold transition-all group-hover:w-3/4 rounded-full"></span>
                </motion.a>
              ))}
            </div>

            {/* CTA Button + WhatsApp + Language Switcher */}
            <div className="hidden lg:flex items-center gap-3">
              <LanguageSwitcher isScrolled={isScrolled} />
              <a
                href="/mon-compte"
                className={`flex items-center justify-center w-10 h-10 rounded-lg font-inter text-sm font-medium transition-all hover:scale-105 ${
                  isScrolled
                    ? 'text-night-blue hover:bg-slate-100'
                    : 'text-white hover:bg-white/10'
                }`}
                aria-label="Mon compte - Espace client"
              >
                <i className="ri-user-line text-xl" aria-hidden="true"></i>
              </a>
              <a
                href="tel:+33605606006"
                className={`flex items-center gap-2 px-4 py-2.5 rounded-lg font-inter text-sm font-medium transition-all hover:scale-105 ${
                  isScrolled
                    ? 'text-night-blue hover:bg-slate-100'
                    : 'text-white hover:bg-white/10'
                }`}
                aria-label="Appeler Voyage Elite au +33 6 05 60 60 06"
              >
                <i className="ri-phone-line text-base" aria-hidden="true"></i>
                <span className="hidden xl:inline">+33 6 05 60 60 06</span>
              </a>
              <a
                href="https://wa.me/33605606006"
                target="_blank"
                rel="noopener noreferrer"
                className={`flex items-center justify-center w-10 h-10 rounded-lg font-inter text-sm font-medium transition-all hover:scale-105 ${
                  isScrolled
                    ? 'text-night-blue hover:bg-slate-100'
                    : 'text-white hover:bg-white/10'
                }`}
                aria-label="Contacter Voyage Elite sur WhatsApp"
              >
                <i className="ri-whatsapp-line text-xl" aria-hidden="true"></i>
              </a>
              <a
                href="/reservation"
                className="px-6 py-2.5 bg-gradient-to-r from-voyage-gold to-amber-500 text-night-blue rounded-full font-inter font-bold text-sm hover:shadow-xl hover:scale-105 transition-all whitespace-nowrap cursor-pointer"
                aria-label="Réserver votre transfert en ligne"
              >
                <strong>Réserver</strong>
              </a>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className={`lg:hidden p-2 rounded-lg transition-colors ${
                isScrolled
                  ? 'text-night-blue hover:bg-slate-100'
                  : 'text-white hover:bg-white/10'
              }`}
              aria-label={isMobileMenuOpen ? 'Fermer le menu' : 'Ouvrir le menu'}
              aria-expanded={isMobileMenuOpen}
              aria-controls="mobile-menu"
            >
              <i className={`text-2xl ${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`} aria-hidden="true"></i>
            </button>
          </div>
        </div>
      </motion.nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            id="mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed top-[72px] left-0 right-0 z-40 bg-white shadow-2xl lg:hidden overflow-hidden"
            role="menu"
          >
            <div className="px-4 py-6 space-y-2 max-h-[calc(100vh-72px)] overflow-y-auto">
              {navLinks.map((link, index) => (
                <motion.a
                  key={link.name}
                  href={link.path}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.05, duration: 0.3 }}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="block px-4 py-3 text-night-blue hover:bg-gradient-to-r hover:from-voyage-gold/10 hover:to-amber-500/10 hover:text-voyage-gold rounded-lg font-inter font-medium transition-all"
                  role="menuitem"
                  aria-label={link.ariaLabel}
                >
                  {link.name}
                </motion.a>
              ))}
              <div className="px-4 py-2 border-t border-slate-200 mt-3 pt-4">
                <LanguageSwitcher isScrolled={true} />
              </div>
              <a
                href="tel:+33605606006"
                className="flex items-center justify-center gap-2 px-4 py-3 text-night-blue hover:bg-slate-100 rounded-lg font-inter font-medium transition-all"
                aria-label="Appeler au +33 6 05 60 60 06"
              >
                <i className="ri-phone-line" aria-hidden="true"></i>
                +33 6 05 60 60 06
              </a>
              <a
                href="https://wa.me/33605606006"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 px-4 py-3 text-white bg-[#25D366] hover:bg-[#20BA5A] rounded-lg font-inter font-medium transition-all"
                aria-label="Contacter sur WhatsApp"
              >
                <i className="ri-whatsapp-line text-xl" aria-hidden="true"></i>
                WhatsApp
              </a>
              <a
                href="/mon-compte"
                className="flex items-center justify-center gap-2 px-4 py-3 text-night-blue hover:bg-slate-100 rounded-lg font-inter font-medium transition-all"
                aria-label="Mon compte"
              >
                <i className="ri-user-line text-xl" aria-hidden="true"></i>
                Mon compte
              </a>
              <a
                href="/reservation"
                className="block text-center px-4 py-3 bg-gradient-to-r from-voyage-gold to-amber-500 text-night-blue rounded-full font-inter font-bold hover:shadow-lg transition-all cursor-pointer"
                aria-label="Réserver maintenant votre transfert"
              >
                <strong>Réserver maintenant</strong>
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
